import { BAPIError } from "@/bapi-client/errors";
import { fetchHandler } from "@/bapi-client/fetch";
import { Result } from "@/bapi-client/types";
import { BAPI_COMMANDS } from "@/bapi-client/types/commands";
import {
  CustomReport,
  CustomReportData,
  BAPIParamsCreateReport,
  CustomReportSchedule,
  BAPIParamReportShare,
  BAPIParamUpdateReport,
  CustomReportPartial,
} from "@/bapi-client/types/report-builder";

export async function getReportBuilderReportList(customerId: string): Promise<Result<CustomReportPartial[]>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_GET_REPORT_LIST,
    "GET",
    `/reports/${customerId}/report`,
  );

  if (!fetchResult.success) {
    return fetchResult;
  }

  const parsed = await fetchResult.data.json();
  return parsed.success ? { success: true, data: parsed.data } : parsed;
}

export async function getReportBuilderReport(customerId: string, reportId: string): Promise<Result<CustomReport>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_GET_REPORT_DETAILS,
    "GET",
    `/reports/${customerId}/report/${reportId}/get`,
  );
  if (!fetchResult.success) return fetchResult;
  const parsed = await fetchResult.data.json();
  if (!parsed.success) return parsed;
  // Did you know that BAPI doesn't return template_name on the report details endpoint?
  // Because I didn't, until now.
  return {
    success: true,
    data: {
      ...parsed.data,
      template_name: parsed.data.template.template_name,
    },
  };
}

export async function getReportDownload(
  customerId: string,
  reportId: string,
  filetype: "csv" | "pdf" | "xlsx",
): Promise<Result<File>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_DOWNLOAD_REPORT,
    "GET",
    `/reports/${customerId}/report/${reportId}/${filetype}`,
  );
  if (!fetchResult.success) return fetchResult;
  const response = fetchResult.data.raw();
  const blob = await response.blob().catch((e) => {
    return { success: false as const, error: new Error(e) };
  });
  if (!(blob instanceof Blob)) return blob;
  const filename = response.headers.get("Content-Disposition")
    ? `${response.headers.get("Content-Disposition")?.split("filename=")[1]?.replaceAll('"', "") ?? "downloaded-file"}`
    : "downloaded-file";

  return { success: true, data: new File([blob], filename, { type: blob.type }) };
}

export async function deleteReport(customerId: string, reportId: string): Promise<Result<void>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_DELETE_REPORT,
    "DELETE",
    `/reports/${customerId}/report/${reportId}`,
  );
  if (!fetchResult.success) return fetchResult;
  return { success: true, data: undefined };
}

export async function getReportData(customerId: string, reportId: string): Promise<Result<CustomReportData>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_GET_REPORT_DATA,
    "GET",
    `/reports/${customerId}/report/${reportId}`,
  );
  if (!fetchResult.success) return fetchResult;
  const parsed = await fetchResult.data.json();
  return parsed.success ? { success: true, data: parsed.data } : parsed;
}

export async function createReport(customerId: string, report: BAPIParamsCreateReport): Promise<Result<CustomReport>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_CREATE_REPORT,
    "POST",
    `/reports/${customerId}/report`,
    { body: report },
  );

  if (!fetchResult.success) return fetchResult;
  const parsed = await fetchResult.data.json();
  return parsed.success ? { success: true, data: parsed.data } : parsed;
}

export async function updateReport(
  customerId: string,
  reportId: string,
  report: BAPIParamUpdateReport,
): Promise<Result<CustomReport>> {
  const fr = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_UPDATE_REPORT,
    "PATCH",
    `/reports/${customerId}/report/${reportId}`,
    { body: report },
  );

  if (!fr.success) return fr;

  const parsed = await fr.data.json();
  return parsed.success ? { success: true, data: parsed.data } : parsed;
}

export async function getReportSchedule(customerId: string, reportId: string): Promise<Result<CustomReportSchedule>> {
  const fetchResult = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_GET_REPORT_SCHEDULE,
    "GET",
    `/reports/${customerId}/report/${reportId}/schedule`,
  );

  if (!fetchResult.success) return fetchResult;

  const parsed = await fetchResult.data.json();
  if (!parsed.success) {
    return parsed;
  }

  // BAPI theoretically supports multiple but we don't actually as a company.
  // Therefore, we only want the "first" one.
  const [sched] = parsed.data.schedules;
  return sched ? { success: true, data: sched } : { success: false, error: new BAPIError("No schedule found", 404) };
}

export async function shareReport(
  customerId: string,
  reportId: string,
  body: BAPIParamReportShare,
): Promise<Result<boolean>> {
  const fr = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_SHARE_REPORT,
    "POST",
    `/reports/${customerId}/report/${reportId}/send`,
    { body },
  );

  if (!fr.success) return fr;

  return { success: true, data: true };
}

export async function saveReportSchedule(
  customerId: string,
  schedule: CustomReportSchedule,
): Promise<Result<CustomReportSchedule>> {
  const { schedule_id, report_id, ...body } = schedule;
  const fr = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_SAVE_REPORT_SCHEDULE,
    "POST",
    `/reports/${customerId}/report/${report_id}/schedule`,
    { body },
  );

  if (!fr.success) return fr;

  const parsed = await fr.data.json();
  if (!parsed.success) {
    return parsed;
  }

  const sched = parsed.data.schedule;
  return { success: true, data: sched };
}

export async function previewReport(
  customerId: string,
  templateId: string,
  body: Pick<BAPIParamsCreateReport, "filters" | "sort_fields">,
): Promise<Result<CustomReportData>> {
  const fr = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_PREVIEW_REPORT,
    "POST",
    `/reports/${customerId}/template/${templateId}/preview`,
    { body },
  );

  if (!fr.success) {
    return fr;
  }

  const parsed = await fr.data.json();
  if (!parsed.success) return parsed;
  return { success: true, data: parsed.data };
}

export async function deleteReportSchedule(
  customerId: string,
  reportId: string,
  scheduleId: string,
): Promise<Result<void>> {
  const fr = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_DELETE_REPORT_SCHEDULE,
    "DELETE",
    `/reports/${customerId}/report/${reportId}/schedule/${scheduleId}`,
  );

  if (!fr.success) return fr;

  return { success: true, data: undefined };
}

export async function favoriteReport(customerId: string, reportId: string, status: boolean): Promise<Result<boolean>> {
  const body = { favorited: status };
  const response = await fetchHandler(
    BAPI_COMMANDS.REPORT_BUILDER_FAVORITE_REPORT,
    "POST",
    `/reports/${customerId}/report/${reportId}/favorite`,
    { body },
  );

  if (!response.success) return response;

  const parsed = await response.data.json();
  return parsed.success ? { success: true, data: true } : parsed;
}
